import React from 'react';
import {
  VStack,
  HStack,
  Text,
  Box,
  Heading,
  Divider,
  Badge,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  Progress,
  useColorModeValue,
  Card,
  CardBody,
  CardHeader,
} from '@chakra-ui/react';
import { useDebugStore } from '../../../../stores/debugStore';

const ProfileViewer: React.FC = () => {
  const { learningProfile } = useDebugStore();
  const cardBg = useColorModeValue('gray.50', 'gray.700');

  const getLearningStyleColor = (style?: string) => {
    switch (style) {
      case 'visual':
        return 'purple';
      case 'auditory':
        return 'blue';
      case 'kinesthetic':
        return 'green';
      case 'mixed':
        return 'orange';
      default:
        return 'gray';
    }
  };

  if (!learningProfile) {
    return (
      <VStack align="stretch" spacing={4}>
        <Heading size="md">Learning Profile</Heading>
        <Box
          p={8}
          textAlign="center"
          borderWidth={1}
          borderColor="gray.200"
          borderRadius="md"
          borderStyle="dashed"
        >
          <Text color="gray.500">No profile data available</Text>
          <Text fontSize="sm" color="gray.400" mt={2}>
            Profile will be loaded when you start chatting
          </Text>
        </Box>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Heading size="md">Learning Profile</Heading>

      <Card bg={cardBg}>
        <CardHeader pb={2}>
          <HStack justify="space-between">
            <Text fontWeight="bold">Learning Style</Text>
            <Badge
              colorScheme={getLearningStyleColor(learningProfile.learning_style)}
              fontSize="sm"
              px={3}
              py={1}
            >
              {learningProfile.learning_style?.toUpperCase() || 'UNKNOWN'}
            </Badge>
          </HStack>
        </CardHeader>
      </Card>

      {learningProfile.learner_summary && (
        <Card bg={cardBg}>
          <CardHeader pb={2}>
            <Text fontWeight="bold">Learner Summary</Text>
          </CardHeader>
          <CardBody pt={0}>
            <Text fontSize="sm" color="gray.600" whiteSpace="pre-wrap">
              {learningProfile.learner_summary}
            </Text>
          </CardBody>
        </Card>
      )}

      {learningProfile.knowledge_domains && Object.keys(learningProfile.knowledge_domains).length > 0 && (
        <Card bg={cardBg}>
          <CardHeader pb={2}>
            <Text fontWeight="bold">Knowledge Domains</Text>
          </CardHeader>
          <CardBody pt={0}>
            <VStack align="stretch" spacing={3}>
              {Object.entries(learningProfile.knowledge_domains).map(([domain, level]) => (
                <Box key={domain}>
                  <HStack justify="space-between" mb={1}>
                    <Text fontSize="sm" textTransform="capitalize">
                      {domain.replace(/_/g, ' ')}
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {Math.round(level * 100)}%
                    </Text>
                  </HStack>
                  <Progress
                    value={level * 100}
                    size="sm"
                    colorScheme={level > 0.7 ? 'green' : level > 0.4 ? 'yellow' : 'red'}
                    borderRadius="full"
                  />
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>
      )}

      {learningProfile.interests && learningProfile.interests.length > 0 && (
        <Card bg={cardBg}>
          <CardHeader pb={2}>
            <Text fontWeight="bold">Interests</Text>
          </CardHeader>
          <CardBody pt={0}>
            <Wrap>
              {learningProfile.interests.map((interest, index) => (
                <WrapItem key={index}>
                  <Tag size="md" colorScheme="blue" borderRadius="full">
                    <TagLabel>{interest}</TagLabel>
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          </CardBody>
        </Card>
      )}

      <Card bg={cardBg}>
        <CardHeader pb={2}>
          <Text fontWeight="bold">Basic Information</Text>
        </CardHeader>
        <CardBody pt={0}>
          <VStack align="stretch" spacing={2}>
            {learningProfile.grade_level && (
              <HStack>
                <Text fontSize="sm" color="gray.600">Grade Level:</Text>
                <Text fontSize="sm" fontWeight="medium">{learningProfile.grade_level}</Text>
              </HStack>
            )}
            {learningProfile.occupation && (
              <HStack>
                <Text fontSize="sm" color="gray.600">Occupation:</Text>
                <Text fontSize="sm" fontWeight="medium">{learningProfile.occupation}</Text>
              </HStack>
            )}
            {learningProfile.favorite_subject && (
              <HStack>
                <Text fontSize="sm" color="gray.600">Favorite Subject:</Text>
                <Text fontSize="sm" fontWeight="medium">{learningProfile.favorite_subject}</Text>
              </HStack>
            )}
            {learningProfile.challenges && (
              <Box>
                <Text fontSize="sm" color="gray.600">Challenges:</Text>
                <Text fontSize="sm" mt={1}>{learningProfile.challenges}</Text>
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default ProfileViewer;